<template>
  <div class="privacidade-page">
    <HomeHeader />

    <!-- Hero Section com Efeito Parallax -->
    <section class="hero-section">
      <div class="parallax-container">
        <div class="overlay"></div>
        <h1 class="hero-title">{{ t('privacyPolicy.heroTitle') }}</h1>
        <div class="hero-subtitle">{{ t('privacyPolicy.heroSubtitle') }}</div>
      </div>
    </section>

    <!-- Seção de Introdução -->
    <section class="intro-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="100">
          <div class="section-title">
            <span class="accent-line"></span>
            <h2>{{ t('privacyPolicy.introTitle') }}</h2>
            <span class="accent-line"></span>
          </div>
          <p class="intro-text">
            {{ t('privacyPolicy.introText') }}
          </p>

          <!-- Contato do DPO -->
          <div class="dpo-contact">
            <div class="dpo-info">
              <h4 class="dpo-title">{{ t('privacyPolicy.dpoTitle') }}</h4>
              <div class="dpo-details">
                <div class="dpo-item">
                  <i class="fas fa-user-shield"></i>
                  <span class="dpo-label">{{ t('privacyPolicy.dpoName') }}:</span>
                  <span class="dpo-value">Ítalo Morais - DPO Uni Hospitalar</span>
                </div>
                <div class="dpo-item">
                  <i class="fas fa-envelope"></i>
                  <span class="dpo-label">{{ t('privacyPolicy.dpoEmail') }}:</span>
                  <a href="mailto:dpo@unihospitalar.com.br" class="dpo-email">
                    dpo@unihospitalar.com.br
                  </a>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <!-- Seção Principal de Política de Privacidade -->
    <section class="privacy-main">
      <div class="container">
        <div class="privacy-grid">
          <ScrollReveal v-for="(section, index) in sections" :key="index"
            :direction="index % 2 === 0 ? 'left' : 'right'" :delay="200 + (index * 100)">
            <div class="privacy-card">
              <div class="privacy-icon" v-html="section.icon"></div>
              <div class="privacy-content">
                <h3>{{ section.title }}</h3>
                <p>{{ section.text }}</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- Seção de Direitos do Usuário -->
    <section class="user-rights-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="section-title light">
            <span class="accent-line"></span>
            <h2>{{ t('privacyPolicy.rightsTitle') }}</h2>
            <span class="accent-line"></span>
          </div>
          <p class="rights-intro">{{ t('privacyPolicy.rightsIntro') }}</p>
        </ScrollReveal>

        <div class="rights-steps">
          <ScrollReveal direction="bottom" :delay="300">
            <div class="rights-step">
              <div class="step-number">
                <i class="fas fa-database"></i>
              </div>
              <div class="step-content">
                <h4>{{ t('privacyPolicy.rights.access.title') }}</h4>
                <p>{{ t('privacyPolicy.rights.access.description') }}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="bottom" :delay="400">
            <div class="rights-step">
              <div class="step-number">
                <i class="fas fa-edit"></i>
              </div>
              <div class="step-content">
                <h4>{{ t('privacyPolicy.rights.correction.title') }}</h4>
                <p>{{ t('privacyPolicy.rights.correction.description') }}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="bottom" :delay="500">
            <div class="rights-step">
              <div class="step-number">
                <i class="fas fa-trash-alt"></i>
              </div>
              <div class="step-content">
                <h4>{{ t('privacyPolicy.rights.deletion.title') }}</h4>
                <p>{{ t('privacyPolicy.rights.deletion.description') }}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="bottom" :delay="600">
            <div class="rights-step">
              <div class="step-number">
                <i class="fas fa-ban"></i>
              </div>
              <div class="step-content">
                <h4>{{ t('privacyPolicy.rights.opposition.title') }}</h4>
                <p>{{ t('privacyPolicy.rights.opposition.description') }}</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- Seção CTA -->
    <section class="cta-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="cta-content">
            <h2>{{ t('privacyPolicy.ctaTitle') }}</h2>
            <p>{{ t('privacyPolicy.ctaText') }}</p>
            <router-link to="/Contato" class="cta-button">{{ t('privacyPolicy.ctaButton') }} <i class="fas fa-arrow-right"></i></router-link>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <HomeFooter />
  </div>
</template>

<script>
import HomeHeader from '@/components/HomeHeader.vue';
import HomeFooter from '@/components/HomeFooter.vue';
import { usePrivacyPolicyTranslation } from '@/composables/usePrivacyPolicyTranslation.js';

export default {
  components: {
    HomeHeader,
    HomeFooter
  },
  setup() {
    const { t, currentLanguage } = usePrivacyPolicyTranslation()

    return {
      t,
      currentLanguage
    }
  },
  data() {
    return {
      selectedLanguage: 'pt',
      sidebarOpen: false
    }
  },
  computed: {
    sections() {
      return [
        {
          icon: '<i class="fas fa-search"></i>',
          title: this.t('privacyPolicy.sections.about.title'),
          text: this.t('privacyPolicy.sections.about.text')
        },
        {
          icon: '<i class="fas fa-shield-alt"></i>',
          title: this.t('privacyPolicy.sections.security.title'),
          text: this.t('privacyPolicy.sections.security.text')
        },
        {
          icon: '<i class="fas fa-bullseye"></i>',
          title: this.t('privacyPolicy.sections.objective.title'),
          text: this.t('privacyPolicy.sections.objective.text')
        },
        {
          icon: '<i class="fas fa-handshake"></i>',
          title: this.t('privacyPolicy.sections.commitment.title'),
          text: this.t('privacyPolicy.sections.commitment.text')
        },
        {
          icon: '<i class="fas fa-lock"></i>',
          title: this.t('privacyPolicy.sections.protection.title'),
          text: this.t('privacyPolicy.sections.protection.text')
        },
        {
          icon: '<i class="fas fa-file-alt"></i>',
          title: this.t('privacyPolicy.sections.treatment.title'),
          text: this.t('privacyPolicy.sections.treatment.text')
        }
      ]
    }
  },
  methods: {
    changeLanguage(event) {
      const value = typeof event === 'string' ? event : event.target.value;
      this.selectedLanguage = value;
      console.log(`Idioma selecionado: ${value}`);
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    }
  }
}
</script>

<style scoped>
/* Estilos Gerais */
.privacidade-page {
  font-family: 'Matter', 'Segoe UI', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

section {
  padding: 80px 0;
}

/* Hero Section com Parallax */
.hero-section {
  position: relative;
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  border-bottom: 4px solid #931414;
}

.parallax-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/header-politica-privacidade.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(147, 20, 20, 0.7), rgba(0, 0, 0, 0.7));
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 2px;
  animation: fadeInUp 1.5s ease-out;
}

.hero-subtitle {
  font-size: 1.5rem;
  font-weight: 300;
  position: relative;
  z-index: 2;
  animation: fadeInUp 1.5s ease-out 0.3s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Seção de Introdução */
.intro-section {
  background-color: #f0f0f0;
  text-align: center;
  position: relative;
  overflow: hidden;
}

/* Símbolo em bordô sangrando pelo canto superior esquerdo */
.intro-section::after {
  content: '';
  position: absolute;
  top: -357px;
  left: -336px;
  width: 1050px;
  height: 1050px;
  background-image: url('@/assets/uni-simbolo-outline.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  mask-image: linear-gradient(135deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(135deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  pointer-events: none;
  opacity: 0.16;
  z-index: 0;
}

.intro-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(240, 240, 240, 0.05) 0px, rgba(240, 240, 240, 0.05) 1px, transparent 1px, transparent 14px);
  z-index: 0;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
}

.section-title h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #931414;
  margin: 0 20px;
}

.section-title.light h2 {
  color: #ffffff;
}

.accent-line {
  height: 3px;
  width: 70px;
  background-color: #931414;
}

.section-title.light .accent-line {
  background-color: #ffffff;
}

.intro-text {
  font-size: 1.3rem;
  max-width: 800px;
  margin: 0 auto;
  color: #555;
  line-height: 1.8;
}

/* Estilos para o contato do dpo */
.dpo-contact {
  margin-top: 40px;
  padding: 30px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 249, 250, 0.98));
  border-radius: 15px;
  border-left: 4px solid #931414;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.dpo-contact:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.dpo-info {
  text-align: center;
}

.dpo-title {
  font-size: 1.4rem;
  color: #931414;
  margin: 0px 0px 20px 0px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.dpo-details {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
}

.dpo-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  color: #555;
}

.dpo-item i {
  color: #931414;
  font-size: 1.2rem;
  width: 20px;
  text-align: center;
}

.dpo-label {
  font-weight: 600;
  color: #333;
}

.dpo-value {
  color: #696969;
  font-weight: 500;
}

.dpo-email {
  color: #931414;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
}

.dpo-email::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: -2px;
  left: 0;
  background-color: #931414;
  transition: width 0.3s ease;
}

.dpo-email:hover {
  color: #470404;
  transform: translateY(-2px);
}

.dpo-email:hover::after {
  width: 100%;
}

/* Seção Principal de Política de Privacidade */
.privacy-main {
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
}

/* Símbolo em dourado sangrando pelo canto inferior direito */
.privacy-main::after {
  content: '';
  position: absolute;
  bottom: -399px;
  right: -357px;
  width: 1092px;
  height: 1092px;
  background-image: url('@/assets/uni-simbolo-outline-dourado.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  mask-image: linear-gradient(315deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(315deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  pointer-events: none;
  opacity: 0.14;
  z-index: 0;
}

.privacy-main::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(240, 240, 240, 0.07) 0px, rgba(240, 240, 240, 0.07) 1px, transparent 1px, transparent 14px);
  opacity: 0.5;
}

.privacy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 40px;
  position: relative;
  z-index: 1;
}

.privacy-card {
  display: flex;
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
}

.privacy-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.privacy-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 120px;
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  font-size: 2.5rem;
  padding: 20px;
  flex-shrink: 0;
}

.privacy-content {
  padding: 30px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.privacy-content h3 {
  font-size: 1.6rem;
  color: #931414;
  margin-bottom: 15px;
  font-weight: 700;
  position: relative;
  padding-bottom: 15px;
  flex-shrink: 0;
}

.privacy-content h3::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 50px;
  height: 3px;
  background-color: #931414;
}

.privacy-content p {
  color: #696969;
  margin-bottom: 0;
  line-height: 1.7;
  font-size: 0.95rem;
  flex-grow: 1;
  display: flex;
  align-items: center;
}

/* Seção de Direitos do Usuário */
.user-rights-section {
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  position: relative;
  overflow: hidden;
}

/* Símbolo em branco, centralizado e expandido */
.user-rights-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/uni-simbolo-outline-branco.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 1300px;
  pointer-events: none;
  opacity: 0.13;
  z-index: 0;
}

.user-rights-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.08) 0px, rgba(255, 255, 255, 0.08) 1px, transparent 1px, transparent 14px);
}

.rights-intro {
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 50px;
}

.rights-steps {
  display: flex;
  flex-direction: column;
  gap: 30px;
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
}

.rights-steps::before {
  content: '';
  position: absolute;
  top: 0;
  left: 30px;
  width: 2px;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.2);
  z-index: -1;
}

.rights-step {
  display: flex;
  align-items: flex-start;
  gap: 30px;
}

.rights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  position: relative;
  z-index: 1;
}

.right-item {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  backdrop-filter: blur(5px);
  transition: transform 0.3s ease, background-color 0.3s ease;
}

.right-item:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.2);
}

.right-icon {
  font-size: 2.5rem;
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.9);
}

.right-item h4 {
  font-size: 1.3rem;
  margin-bottom: 15px;
  color: white;
}

.right-item p {
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.step-number {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.step-number i {
  font-size: 1.8rem;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-5px);
  }

  100% {
    transform: translateY(0px);
  }
}

.step-content {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  padding: 25px;
  border-radius: 10px;
  flex: 1;
}

.step-content h4 {
  font-size: 1.3rem;
  margin-bottom: 10px;
  color: white;
}

.step-content p {
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.rights-step:hover .step-number {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.rights-step:hover .step-number i {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}

.rights-step:nth-child(1) .step-number {
  border-color: rgba(71, 4, 4, 0.5);
}

.rights-step:nth-child(2) .step-number {
  border-color: rgba(85, 150, 255, 0.5);
}

.rights-step:nth-child(3) .step-number {
  border-color: rgba(85, 255, 157, 0.5);
}

.rights-step:nth-child(4) .step-number {
  border-color: rgba(255, 204, 85, 0.5);
}

/* Seção CTA */
.cta-section {
  background: linear-gradient(135deg, #f0f0f0, #f0f0f0);
  text-align: center;
  padding: 100px 0;
}

.cta-content {
  max-width: 700px;
  margin: 0 auto;
}

.cta-content h2 {
  font-size: 2.5rem;
  color: #470404;
  margin-bottom: 20px;
  font-weight: 700;
}

.cta-content p {
  font-size: 1.2rem;
  color: #696969;
  margin-bottom: 30px;
}

/* .cta-button está na regra global de glass, mas o fundo aqui
   (.cta-section) é cinza chapado, então precisa de opacidade alta pra
   não ficar sujo/desbotado */
.cta-button {
  position: relative;
  overflow: hidden;
  display: inline-block;
  padding: 15px 40px;
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.32), rgba(71, 4, 4, 0.32)) !important;
  backdrop-filter: blur(20px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(200%) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(147, 20, 20, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.cta-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.cta-button i {
  position: relative;
  margin-left: 7px;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
}

.cta-button:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(147, 20, 20, 0.4);
}

/* Responsividade */
@media (max-width: 1200px) {
  .container {
    padding: 0 40px;
  }

  .privacy-grid {
    grid-template-columns: 1fr;
  }

  .privacy-card {
    max-width: 100%;
  }
}

@media (max-width: 992px) {
  .hero-title {
    font-size: 3rem;
  }

  .hero-subtitle {
    font-size: 1.3rem;
  }

  section {
    padding: 60px 0;
  }

  .privacy-card {
    flex-direction: column;
  }

  .privacy-icon {
    min-width: 100%;
    padding: 30px 0;
  }

  .privacy-content {
    padding: 25px;
  }

  .security-content {
    flex-direction: column;
    text-align: center;
  }

  .security-text h3::after {
    left: 50%;
    transform: translateX(-50%);
  }

  .security-features li {
    text-align: left;
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 60vh;
    min-height: 400px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .section-title h2 {
    font-size: 2rem;
  }

  .privacy-content h3 {
    font-size: 1.4rem;
  }

  .privacy-content p {
    font-size: 0.9rem;
  }

  .intro-text {
    font-size: 1.1rem;
  }

  .cta-content h2 {
    font-size: 2rem;
  }

  .rights-steps::before {
    display: none;
  }

  .step-number {
    margin: 0 auto;
  }

  .step-content {
    text-align: center;
  }

  .dpo-contact {
    margin-top: 30px;
    padding: 25px 20px;
  }
  
  .dpo-title {
    font-size: 1.2rem;
  }
  
  .dpo-details {
    gap: 12px;
  }
  
  .dpo-item {
    font-size: 1rem;
    flex-wrap: wrap;
    justify-content: center;
    text-align: center;
  }
}

@media (max-width: 576px) {
  .container {
    padding: 0 20px;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .section-title h2 {
    font-size: 1.8rem;
  }

  .accent-line {
    width: 40px;
  }

  .privacy-content h3 {
    font-size: 1.4rem;
  }

  .rights-step {
    flex-direction: column;
    gap: 15px;
  }

  .dpo-item {
    flex-direction: column;
    gap: 5px;
  }
  
  .dpo-email {
    word-break: break-all;
  }
}

/* Animações adicionais */
.privacy-icon i,
.right-icon i {
  animation: float 3s ease-in-out infinite;
}

/* Animação para os cards de privacidade quando em hover */
.privacy-card:hover .privacy-icon {
  transform: scale(1.05);
  transition: transform 0.3s ease;
}

/* Animação para o botão CTA */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(147, 20, 20, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0);
  }
}

.privacy-card:nth-child(odd) {
  border-top: 4px solid #931414;
}

.privacy-card:nth-child(even) {
  border-top: 4px solid #2C5EAE;
}

.privacy-icon i {
  transition: transform 0.5s ease;
}

.privacy-card:hover .privacy-icon i {
  transform: scale(1.2);
}

.cta-button:hover {
  animation: pulse 1.5s infinite;
}

/* Efeito de destaque para os itens de direitos */
.right-item:nth-child(1) {
  border-bottom: 3px solid rgba(71, 4, 4, 0.5);
}

.right-item:nth-child(2) {
  border-bottom: 3px solid rgba(85, 150, 255, 0.5);
}

.right-item:nth-child(3) {
  border-bottom: 3px solid rgba(85, 255, 157, 0.5);
}

.right-item:nth-child(4) {
  border-bottom: 3px solid rgba(255, 204, 85, 0.5);
}

/* Efeito de hover para os itens de direitos */
.right-item:hover .right-icon {
  transform: scale(1.2);
  transition: transform 0.3s ease;
}
</style>
